import { create } from 'zustand';

import { de } from '../i18n/de';
import { useComposerStore } from '../core/composer/useComposer';
import { ActivateButton } from './ActivateButton';

/**
 * `Zurückholen` — the sentence a hold just cleared, offered back (`SUR-26`, ADR-043).
 *
 * ── Why the hold needs this at all ──
 *
 * `SUR-04`'s argument is a *position*: the thing that clears everything is not the thing next to the thing
 * that removes a typo. A two-second hold on the output bar satisfies that — it is the far end of the screen
 * from `Wort löschen`. But far away is not the same as recoverable, and the bar is 132 pt tall and 60 % wide
 * in the upper middle: a far likelier resting place for a hand holding an iPad than the corner
 * `useCornerLongPress` was designed against. Stillness plus two seconds reduces the accident; it does not
 * make losing a minute of typing survivable.
 *
 * ── Why a tenant rather than a control in the band's frame ──
 *
 * ADR-041 put `KeepOffer` in the frame, and the frame cannot take a third occupant: both conditional ones can
 * be up at once — hold-clear after speaking with `keepTextAfterSpeak` on — which takes five flex units to
 * seven, and at a 40 % content band that puts a repair phrase near 11 mm, under the floor. As a tenant this
 * costs nothing when absent and displaces the repair row, which is the right thing to displace: repair
 * phrases are for *between* utterances and this is mid-correction.
 *
 * ── Why it reaches `undo()` rather than keeping its own copy ──
 *
 * `clear()` goes through `push`, so the cleared text is already on the undo stack. Using it means `CMP-04` —
 * *"the highest-consequence undo is the one that brings back a sentence that was cleared by mistake"* —
 * stops being a claim about a function no UI calls, and the caret comes back where it was for free.
 *
 * The state is in a store for the reason `KeepOffer` gives: `PredictionRow` owns the band and has to know
 * whether the offer applies *before* deciding to render it, and a component cannot answer the question that
 * decides whether it mounts.
 */

/** How long the offer stands. Long enough to notice the bar went empty, short enough not to become furniture. */
const OFFER_MS = 12_000;

interface OfferState {
  /** The text that was cleared, or null. Only ever compared, never rendered. */
  cleared: string | null;
}

const useOfferState = create<OfferState>(() => ({ cleared: null }));

let expiry: ReturnType<typeof setTimeout> | null = null;

/** Raise the offer. Called by the hold, immediately after `clear()`. */
export function offerUndoClear(cleared: string): void {
  if (expiry) clearTimeout(expiry);
  useOfferState.setState({ cleared });
  expiry = setTimeout(() => useOfferState.setState({ cleared: null }), OFFER_MS);
}

/** Withdraw it. */
export function dismissUndoClear(): void {
  if (expiry) clearTimeout(expiry);
  expiry = null;
  useOfferState.setState({ cleared: null });
}

/**
 * Whether the offer applies. Read by the band's owner to decide the tenant.
 *
 * **Withdrawn as soon as anything is typed**, on the same reasoning as `keepStillOffered`: a person who has
 * started a new sentence has accepted the clear, and an offer to bring the old one back would then insert it
 * into what they are writing now.
 */
export const useUndoClearOffer = (): boolean => {
  const cleared = useOfferState((state) => state.cleared);
  const text = useComposerStore((state) => state.state.text);
  return cleared !== null && text === '';
};

export function UndoClearOffer(): React.JSX.Element | null {
  const offered = useUndoClearOffer();
  const undo = useComposerStore((s) => s.undo);

  if (!offered) return null;

  return (
    <ActivateButton
      id="action:undoClear"
      onActivate={() => {
        undo();
        dismissUndoClear();
      }}
      label={de.undoClearName}
      style={{
        flex: '1 1 0',
        minWidth: 0,
        height: '100%',
        fontSize: 'var(--font-button)',
        fontWeight: 700,
        background: 'var(--key-bg)',
        color: 'var(--key-fg)',
        borderWidth: 4,
        borderColor: 'var(--key-border)',
        // Dashed, like `KeepOffer`: shape says *here for a moment and then gone*, which reads without
        // relying on colour (§5.2, `GEO-15`).
        borderStyle: 'dashed',
        borderRadius: 'var(--radius)',
      }}
    >
      {de.undoClear}
    </ActivateButton>
  );
}
